import { type ImgHTMLAttributes, useCallback } from "react";
import { Link } from "react-router";
import type { ImageProps, MediaContent } from "@app/types/Cue";
import { CaaSImageFilters } from "@app/types/OptimisedImage";
import OptimisedImage from "@components/OptimisedImage";
import { getFormattedCaaSImageUrl } from "@components/OptimisedImage/helper";
import cx from "classnames";

import { IS_LAZY_LOAD_DEFAULT_VALUE } from "./utils/constants";

export type ArticleThumbnailProps = {
  rootClassName?: string;
  imgRootClassName?: string;
  thumbnail?:
    | (Partial<MediaContent & ImageProps> & {
        alt?: string;
        caption?: string;
        credit?: string;
      })
    | null;
  orientation?: "landscape" | "freecrop" | "original" | "portrait" | "square";
  filters?: CaaSImageFilters[];
  landscapeThumbnailRootClassName?: string;
  landscapeImgRootClassName?: string;
  invisibleImgRootClass?: string;
  displayBlurBg?: boolean;
  loading?: ImgHTMLAttributes<HTMLImageElement>["loading"];
  articleLink?: string;
  preloadLCPImage?: boolean;
};

export default function ArticleThumbnail({
  rootClassName = "",
  imgRootClassName = "",
  thumbnail = null,
  orientation = "original",
  filters,
  landscapeThumbnailRootClassName = "",
  landscapeImgRootClassName = "",
  invisibleImgRootClass = "",
  displayBlurBg = false,
  loading = IS_LAZY_LOAD_DEFAULT_VALUE,
  articleLink,
  preloadLCPImage = false,
}: ArticleThumbnailProps): React.ReactElement {
  const getThumbnailData = useCallback(() => {
    // TODO this is legacy function to handle different thumbnail format
    // Ideally we should have a unified format for thumbnail
    if (thumbnail?.url) {
      return {
        [orientation]: thumbnail,
      };
    }

    if (thumbnail?.poster) {
      return thumbnail?.poster?.[0]?.content?.fields;
    }

    return thumbnail?.fields;
  }, [thumbnail, orientation]);

  const thumbnailData = getThumbnailData();

  const isImageLandscape = () => {
    const imgW =
      thumbnailData &&
      thumbnailData?.[`${orientation}-caas`] &&
      thumbnailData?.[`${orientation}-caas`]?.width
        ? thumbnailData?.[`${orientation}-caas`]?.width || 0
        : 0;
    const imgH =
      thumbnailData &&
      thumbnailData?.[`${orientation}-caas`] &&
      thumbnailData?.[`${orientation}-caas`]?.height
        ? thumbnailData?.[`${orientation}-caas`]?.height || 0
        : 0;

    return imgW > imgH;
  };

  const thumbnailRootClass =
    isImageLandscape() && landscapeThumbnailRootClassName !== ""
      ? landscapeThumbnailRootClassName
      : rootClassName;

  const thumbnailSrc =
    thumbnailData && thumbnailData?.[`${orientation}-caas`]
      ? thumbnailData?.[`${orientation}-caas`]?.url
      : thumbnailData?.[orientation]?.url;

  const imgHTMLProps: ImgHTMLAttributes<HTMLImageElement> = {
    loading: loading,
    src: thumbnailSrc,
    alt: thumbnail?.alt || thumbnail?.caption || thumbnailData?.caption || "",
    className:
      isImageLandscape() && landscapeImgRootClassName !== ""
        ? landscapeImgRootClassName
        : imgRootClassName,
  };

  const thumbNailSrcWithFilters = getFormattedCaaSImageUrl(thumbnailSrc, {
    f: "webp",
  });

  return (
    <div className={cx(thumbnailRootClass)}>
      {thumbnailData && thumbnailData[`${orientation}-caas`] ? (
        <div
          className={cx(
            isImageLandscape() && landscapeImgRootClassName !== ""
              ? landscapeImgRootClassName
              : invisibleImgRootClass
          )}
          style={{ position: "relative" }}
        >
          {articleLink ? (
            <Link to={articleLink} target="_blank" rel="noopener">
              <OptimisedImage
                {...imgHTMLProps}
                filters={filters}
                preloadLCPImage={preloadLCPImage}
              />
            </Link>
          ) : (
            <OptimisedImage
              {...imgHTMLProps}
              filters={filters}
              preloadLCPImage={preloadLCPImage}
            />
          )}
        </div>
      ) : articleLink ? (
        <Link to={articleLink} target="_blank" rel="noopener">
          <picture>
            <img {...imgHTMLProps} />
          </picture>
        </Link>
      ) : (
        <picture>
          <img {...imgHTMLProps} />
        </picture>
      )}

      {displayBlurBg && thumbnailData ? (
        <div
          style={{
            background: `center / cover no-repeat url(${thumbNailSrcWithFilters})`,
          }}
          className="absolute top-0 z-[-1] h-full w-full overflow-hidden blur-[100px]
          "
        />
      ) : null}
    </div>
  );
}
